import type { IsoDate, Lead, Signal } from '../../../shared/types';
import {
  HIDE_REASON_LABELS,
  SCORE_PART_LABELS,
  SIGNAL_KIND_SINGULAR,
  TRIAGE_FLAG_LABELS,
} from '../ui/labels';
import { diffDays, formatDate } from './dates';
import { formatDistance, haversineMeters } from './route';
import type { LatLng } from './route';

/** How the page reads the sort (D72): the sections of the list, the why line and the hidden line of a
 *  card, how long ago a license was filed. Pure. */

export const TOP_SHOWN = 10;
export const PAGE = 20;
/** Near you (D80): the rest within this of the phone's position or the day route's anchor, nearest first. */
export const NEAR_MILES = 3;
const NEAR_METERS = NEAR_MILES * 1609.344;
export const NEAR_SHOWN = 10;
/** Leads nearby on the desk: the rep's leads within this of the business. */
export const NEARBY_METERS = 500;

export interface Sections {
  /** Worth a visit today: the first ten rows the server put on top. */
  top: Signal[];
  /** Near you: the rest within three miles, nearest first, ten; empty without an anchor or coordinates. */
  near: Signal[];
  /** More to review: every other row of the queue, in the server's order (top overflow first). */
  more: Signal[];
  hidden: Signal[];
}

const placed = (s: Pick<Signal, 'lat' | 'lng'>): s is { lat: number; lng: number } =>
  typeof s.lat === 'number' && typeof s.lng === 'number';

/** How far a business is from a point, in meters; null when either is unplaced (a copy stored before
 *  coordinates has none). */
export const metersTo = (s: Pick<Signal, 'lat' | 'lng'>, from: LatLng | null): number | null =>
  from && placed(s) ? haversineMeters(from.lat, from.lng, s.lat, s.lng) : null;

/** "2.1 mi", or null with nothing to measure from or to. */
export const distanceText = (s: Pick<Signal, 'lat' | 'lng'>, from: LatLng | null): string | null => {
  const meters = metersTo(s, from);
  return meters === null ? null : formatDistance(meters);
};

/** The sections: the ten on top as the server ranks them (their distance shows where they are), then
 *  Near you drawn from the rest, then the rest without those. */
export const sectionsOf = (
  list: { signals: readonly Signal[]; hidden: readonly Signal[] },
  anchor: LatLng | null = null,
): Sections => {
  const top: Signal[] = [];
  const rest: Signal[] = [];
  for (const s of list.signals) (s.bucket === 'top' && top.length < TOP_SHOWN ? top : rest).push(s);
  const near = rest
    .map((s) => ({ s, meters: metersTo(s, anchor) }))
    .filter((x): x is { s: Signal; meters: number } => x.meters !== null && x.meters <= NEAR_METERS)
    .sort((a, b) => a.meters - b.meters)
    .slice(0, NEAR_SHOWN)
    .map((x) => x.s);
  return { top, near, more: rest.filter((s) => !near.includes(s)), hidden: [...list.hidden] };
};

/** The rep's leads within reach of a business, nearest first, among those the phone has placed. */
export const leadsNearby = (
  leads: readonly Lead[],
  placedAt: ReadonlyMap<string, LatLng>,
  from: LatLng,
  meters = NEARBY_METERS,
): { lead: Lead; meters: number }[] =>
  leads
    .flatMap((lead) => {
      const at = placedAt.get(lead.id);
      if (!at) return [];
      const d = haversineMeters(from.lat, from.lng, at.lat, at.lng);
      return d <= meters ? [{ lead, meters: d }] : [];
    })
    .sort((a, b) => a.meters - b.meters);

/** "Why: new storefront, filed recently, has a phone, same address as Old Cafe — city unverified"; the
 *  caveat alone when nothing speaks for the row; nothing when unsorted. */
export const whyText = (s: Signal): string | null => {
  const t = s.triage;
  if (!t) return null;
  const reasons = t.parts.filter((p) => p.points > 0).map((p) => SCORE_PART_LABELS[p.code]);
  if (t.same_address) reasons.push(`same address as ${t.same_address.name}`);
  const caveats = (t.flags ?? []).map((f) => TRIAGE_FLAG_LABELS[f]).join(', ');
  if (reasons.length) return `Why: ${reasons.join(', ')}${caveats ? ` — ${caveats}` : ''}`;
  return caveats ? caveats.charAt(0).toUpperCase() + caveats.slice(1) : null;
};

/** "Hidden: chain (7-Eleven)", "Hidden by you", "Later, until Nov 16"; nothing for a card in the open. */
export const hiddenText = (s: Signal): string | null => {
  const hide = s.triage?.hide;
  if (!hide) return null;
  if (hide.reason === 'rep') return HIDE_REASON_LABELS.rep;
  if (hide.reason === 'later')
    return s.hidden_until
      ? `Later, until ${formatDate(s.hidden_until.slice(0, 10))}`
      : HIDE_REASON_LABELS.later;
  return `Hidden: ${HIDE_REASON_LABELS[hide.reason]}${hide.value ? ` (${hide.value})` : ''}`;
};

/** "Filed 6 d ago (Oct 11)", and whether that is past 60 days (a filing date is not an opening date). */
export const filedAgo = (filed_on: IsoDate, today: IsoDate): { text: string; old: boolean } => {
  const days = diffDays(today, filed_on);
  const when = formatDate(filed_on, today);
  return { text: days <= 0 ? `Filed today (${when})` : `Filed ${days} d ago (${when})`, old: days > 60 };
};

/** "Restaurant · Type 41, beer & wine". */
export const eventText = (s: Signal): string =>
  [SIGNAL_KIND_SINGULAR[s.kind], s.detail].filter(Boolean).join(' · ');
